<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import Tag from 'primevue/tag';
import { navEntries, clearNavHistory } from '@/lib/navHistory';

// Left, non-modal recent-views drawer (same Drawer component as HelpDrawer / the
// recovery drawer). The list is the module-level MRU history (localStorage-backed);
// this component is purely presentational + navigation. Hosted at the app root so its
// open state and content survive AppShell's per-route remount and the drawer can be
// opened/closed without losing anything.
const props = defineProps<{ visible: boolean }>();
const emit = defineEmits<{ 'update:visible': [boolean] }>();

const internalVisible = computed<boolean>({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

const router = useRouter();
const route = useRoute();

// entries[0] is "where you are now" (just recorded); the rest is the back-history.
const currentPath = computed(() => route.fullPath);

function go(path: string) {
  if (path !== currentPath.value) {
    void router.push(path);
  }
  // Drawer stays open (non-modal, app-root) so the operator can keep hopping views.
}
</script>

<template>
  <Drawer
    :visible="internalVisible"
    position="left"
    :modal="false"
    :show-close-icon="false"
    :dismissable="false"
    :close-on-escape="false"
    :block-scroll="false"
    class="nav-history-drawer"
    :style="{ width: '22rem', maxWidth: '90vw' }"
    @update:visible="(v: boolean) => (internalVisible = v)"
  >
    <template #header>
      <div class="header">
        <span class="title">
          <i class="pi pi-history" aria-hidden="true" />
          Recent views
        </span>
        <div class="header-actions">
          <Button
            label="Clear"
            icon="pi pi-trash"
            severity="secondary"
            size="small"
            text
            :disabled="navEntries.length === 0"
            title="Clear the navigation history"
            @click="clearNavHistory"
          />
          <Button
            icon="pi pi-times"
            severity="secondary"
            size="small"
            text
            rounded
            aria-label="Close recent views"
            title="Close"
            @click="internalVisible = false"
          />
        </div>
      </div>
    </template>

    <p v-if="navEntries.length === 0" class="empty">
      No views visited yet this session. As you navigate, the places you've been collect
      here — newest first — so you can jump back with one click.
    </p>

    <ul v-else class="entry-list">
      <li
        v-for="entry in navEntries"
        :key="entry.path"
        :class="['entry', { current: entry.path === currentPath }]"
      >
        <button type="button" class="entry-open" :title="entry.path" @click="go(entry.path)">
          <span class="entry-label">{{ entry.label }}</span>
          <span class="entry-path mono">{{ entry.path }}</span>
        </button>
        <Tag v-if="entry.path === currentPath" value="here" severity="info" rounded />
      </li>
    </ul>
  </Drawer>
</template>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
}
.title { display: inline-flex; align-items: center; gap: 0.5rem; font-weight: 600; }
.header-actions { display: flex; align-items: center; gap: 0.25rem; }
.empty {
  margin: 0;
  color: var(--retail-hint-text);
  font-size: 0.8125rem;
  line-height: 1.5;
}
.entry-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem; }
.entry {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 4px;
  background: var(--p-content-background, #fff);
}
.entry.current { border-color: var(--p-primary-color, #2563eb); }
.entry-open {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.5rem 0.65rem;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
}
.entry-open:hover { background: var(--p-highlight-background); color: var(--p-highlight-color); }
.entry-open:hover .entry-path { color: inherit; }
.entry-label { font-size: 0.875rem; font-weight: 500; word-break: break-word; }
.entry-path { font-size: 0.7rem; color: var(--retail-hint-text); word-break: break-all; }
.mono { font-family: 'Cascadia Code', Consolas, monospace; }
</style>
